




import Vue from "vue";
import eventBus from "@/services/eventBus";
import PowerBIApi from "../services/PowerBIApi";

/**
 * A ?head=<id> query-paraméter Power BI alap-szűrője: a kivonat/karton "BI Riport" gombja a
 * hlboktmpext fej-azonosítót adja át, a riport így csak az adott futás (ilboktmpext.head = id) sorait mutatja.
 * ⚠ A tábla/oszlop név a .pbix adatmodell nevével KELL egyezzen (a riport készítőjével egyeztetve);
 * ha ott másképp hívják a fej-táblát vagy az azonosító oszlopot, ITT kell átírni.
 */
const HEAD_FILTER_TABLE = "hlboktmpext";
const HEAD_FILTER_COLUMN = "szam";

const POWERBI_CLIENT_SRC = "https://cdnjs.cloudflare.com/ajax/libs/powerbi-client/2.18.7/powerbi.min.js";
const POWERBI_CLIENT_SCRIPT_ID = "powerbi-client-script";

interface Data {
  url: string;
  reportId: string;
  paramId: number | null;
}

interface Computed {
  routeParamId: string;
}

interface Method {
  loadPowerBiClient(): Promise<void>;
  buildFilters(): any[];
  getReport(): void;
}

export default Vue.extend<Data, Method, Computed, {}>({
  methods: {
    // A powerbi-client CDN-ről töltődik; a korábbi kód nem várta meg a betöltést, így gyors API-válasznál
    // a window.powerbi még nem létezett. Betöltés után a Promise felold, újrahíváskor azonnal.
    loadPowerBiClient() {
      return new Promise<void>((resolve, reject) => {
        if ((window as any).powerbi) {
          resolve();
          return;
        }
        const existing = document.getElementById(POWERBI_CLIENT_SCRIPT_ID) as HTMLScriptElement | null;
        const scriptTag = existing ?? document.createElement("script");
        scriptTag.addEventListener("load", () => resolve());
        scriptTag.addEventListener("error", () => reject(new Error("A Power BI kliens betöltése nem sikerült.")));
        if (!existing) {
          scriptTag.src = POWERBI_CLIENT_SRC;
          scriptTag.id = POWERBI_CLIENT_SCRIPT_ID;
          document.getElementsByTagName("head")[0].appendChild(scriptTag);
        }
      });
    },

    // Query-string → Power BI alap-szűrők. Ma egy szűrő van (head); további képernyők ide vehetik fel a sajátjukat.
    buildFilters() {
      const filters: any[] = [];
      const head = Number(this.$route.query.head);
      if (head > 0) {
        filters.push({
          $schema: "http://powerbi.com/product/schema#basic",
          target: { table: HEAD_FILTER_TABLE, column: HEAD_FILTER_COLUMN },
          operator: "In",
          values: [head],
          filterType: 1, // models.FilterType.Basic
        });
      }
      return filters;
    },

    async getReport() {
      this.reportId = this.$route.params.id;
      this.paramId = Number(this.$route.params.paramId);

      try {
        const [model] = await Promise.all([
          PowerBIApi.get(this.reportId, this.paramId),
          this.loadPowerBiClient(),
        ]);

        const reportContainer = document.getElementById("embedContainer");
        this.url = model.embedReports[0].embedUrl;

        const config = {
          type: "report",
          tokenType: 1, // models.TokenType.Embed
          accessToken: model.embedToken.token,
          embedUrl: model.embedReports[0].embedUrl,
          id: model.embedReports[0].reportId,
          permissions: 7, // models.Permissions.All
          filters: this.buildFilters(),
          settings: {
            // Enable this setting to remove gray shoulders from embedded report
            background: 1,
            // pmsyspowerbireport.filter_panel_off / nav_con_panel_off → a hívó ?filterPane=0 / ?navPane=0-val küldi;
            // paraméter nélkül (pl. CashFlow) mindkét panel bekapcsolva, ahogy eddig.
            filterPaneEnabled: this.$route.query.filterPane !== "0",
            navContentPaneEnabled: this.$route.query.navPane !== "0",
          },
        };
        (window as any).powerbi.embed(reportContainer, config);
      } catch (error) {
        // A backend BusinessRuleException-jét (lejárt Azure-kulcs, hiányzó paraméterek) a handleApiResponse
        // már kiírta; a kliens-betöltési hibát itt jelezzük.
        if (error instanceof Error && error.message) {
          eventBus.$emit("error", error.message);
        }
        console.error(error);
      }
    },
  },
  data() {
    return {
      url: "",
      reportId: "",
      paramId: null,
    };
  },

  computed: {
    routeParamId() {
      return this.$route.params.id;
    },
  },

  watch: {
    routeParamId() {
      this.getReport();
    },
  },

  async mounted() {
    this.getReport();
  },
});
